import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Clock, TrendingUp, BarChart3, Users, Activity, RefreshCw, PlusCircle, GitCompare } from 'lucide-react';
import { apiGet, adminPost } from '../data/api';
import PlaceBetModal from '../components/PlaceBetModal';
import { AIAdvisorMatch } from '../components/AIAdvisor';

function fmt(v, dec = 2) { return v > 0 ? v.toFixed(dec) : '—'; }
function fmtPct(v) { return v != null ? `${v > 0 ? '+' : ''}${v.toFixed(1)}%` : '—'; }
function fmtProb(v) { return v > 0 ? `${(v * 100).toFixed(1)}%` : '—'; }

export default function TTMatchDetail() {
  const { matchId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);
  const [totalsData, setTotalsData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRec, setModalRec] = useState(null);

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      apiGet(`/tt/matches/${encodeURIComponent(matchId)}`),
      apiGet(`/tt/matches/${encodeURIComponent(matchId)}/totals`).catch(() => null),
    ]).then(([matchData, totals]) => {
      setData(matchData);
      setTotalsData(totals);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [matchId]);

  const handleRefresh = async () => {
    const pin = sessionStorage.getItem('admin_pin');
    if (!pin) { setRefreshResult({ error: 'Accedi come admin prima' }); return; }
    setRefreshing(true);
    setRefreshResult(null);
    try {
      const res = await adminPost(`/tt/admin/refresh-match/${encodeURIComponent(matchId)}`, pin);
      setRefreshResult(res.result);
      fetchData();
    } catch (e) {
      setRefreshResult({ error: e.message });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-zinc-500" size={24} /></div>;
  if (!data || !data.match) return <div className="text-zinc-500 text-center py-20">Match non trovato</div>;

  const { match, odds_history, features, model_output: mo, h2h_history, h2h_summary, player1_recent, player2_recent, lr_prediction: lr } = data;
  const latestOdds = odds_history?.[0] || {};

  return (
    <div data-testid="tt-match-detail" className="space-y-5 max-w-[1100px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/tt/matches" className="text-zinc-500 hover:text-zinc-300"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="text-xl font-bold text-zinc-50" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {match.player1_name} <span className="text-zinc-500">vs</span> {match.player2_name}
            </h1>
            <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
              <span>{match.tournament_name}</span>
              <span className="text-zinc-700">|</span>
              <span className="flex items-center gap-1"><Clock size={11} />{match.match_datetime ? new Date(match.match_datetime).toLocaleString('it-IT') : '—'}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                match.status === 'scheduled' ? 'bg-emerald-500/15 text-emerald-400' :
                match.status === 'live' ? 'bg-blue-500/15 text-blue-400' : 'bg-zinc-500/15 text-zinc-400'
              }`}>{match.status}</span>
            </div>
          </div>
        </div>
        <button
          data-testid="refresh-match-btn"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors disabled:opacity-50"
          style={{ border: '1px solid #27272A', color: refreshing ? '#71717A' : '#A1A1AA' }}
        >
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Aggiornamento...' : 'Aggiorna Match'}
        </button>
      </div>

      {refreshResult && (
        <div className={`rounded-sm px-4 py-2 text-xs ${refreshResult.error ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
          {refreshResult.error
            ? refreshResult.error
            : `Aggiornato: odds=${refreshResult.odds_updated ? 'si' : 'no'}, H2H=${refreshResult.h2h_records || 0}, modello=${refreshResult.model_recalculated ? 'si' : 'no'}`
          }
        </div>
      )}

      {/* Model Output Cards */}
      {mo && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PlayerCard
            player={match.player1_name}
            side="P1"
            bkOdd={mo.bookmaker_odd_p1}
            fairOdd={mo.fair_odd_p1}
            fairProb={mo.final_prob_p1}
            baselineProb={mo.baseline_prob_p1}
            propProb={mo.proprietary_prob_p1}
            edge={mo.edge_p1_pct}
            ev={mo.ev_p1_pct}
            isBest={(mo.ev_p1_pct || 0) >= (mo.ev_p2_pct || 0)}
          />
          <PlayerCard
            player={match.player2_name}
            side="P2"
            bkOdd={mo.bookmaker_odd_p2}
            fairOdd={mo.fair_odd_p2}
            fairProb={mo.final_prob_p2}
            baselineProb={mo.baseline_prob_p2}
            propProb={mo.proprietary_prob_p2}
            edge={mo.edge_p2_pct}
            ev={mo.ev_p2_pct}
            isBest={(mo.ev_p2_pct || 0) > (mo.ev_p1_pct || 0)}
          />
        </div>
      )}

      {/* Confidence and Model Info */}
      {mo && (
        <div className="rounded-sm p-4" style={{ background: '#18181B', border: '1px solid #27272A' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
              <BarChart3 size={13} /> Dettaglio Modello
            </h3>
            <span className="text-xs text-zinc-600">v{mo.model_version}</span>
          </div>

          {/* Probabilities row */}
          <div className="grid grid-cols-3 gap-4 text-xs mb-4">
            <div>
              <span className="text-zinc-500">Prob. Baseline</span>
              <div className="font-mono text-zinc-300 mt-1">{fmtProb(mo.baseline_prob_p1)} / {fmtProb(mo.baseline_prob_p2)}</div>
            </div>
            <div>
              <span className="text-zinc-500">Prob. Proprietaria</span>
              <div className="font-mono text-zinc-300 mt-1">{fmtProb(mo.proprietary_prob_p1)} / {fmtProb(mo.proprietary_prob_p2)}</div>
            </div>
            <div>
              <span className="text-zinc-500">Prob. Finale</span>
              <div className="font-mono text-zinc-200 font-semibold mt-1">{fmtProb(mo.final_prob_p1)} / {fmtProb(mo.final_prob_p2)}</div>
            </div>
          </div>

          {/* Confidence breakdown */}
          <div className="pt-3 mb-3" style={{ borderTop: '1px solid #27272A' }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div data-testid="prediction-confidence">
                <span className="text-zinc-500">Prediction Confidence</span>
                <div className="mt-1"><ConfBar score={mo.confidence_score} /></div>
              </div>
              <div data-testid="data-quality">
                <span className="text-zinc-500">Data Quality</span>
                <div className="mt-1"><ConfBar score={mo.data_quality_score} color="blue" /></div>
              </div>
              <div data-testid="signal-strength">
                <span className="text-zinc-500">Signal Strength</span>
                <div className="mt-1"><ConfBar score={mo.signal_strength_score} color="amber" /></div>
              </div>
              <div data-testid="feature-agreement">
                <span className="text-zinc-500">Feature Agreement</span>
                <div className="mt-1"><ConfBar score={mo.feature_agreement_score} color="purple" /></div>
              </div>
            </div>
            {mo.confidence_explanation && (
              <div className="mt-2.5 px-2.5 py-1.5 rounded bg-zinc-800/60 text-[11px] text-zinc-400" data-testid="confidence-explanation">
                {mo.confidence_explanation}
              </div>
            )}
            {/* Debug row for penalties/sample */}
            <div className="flex gap-4 mt-2 text-[10px] text-zinc-600">
              {mo.sample_size_score != null && <span>Campione: {(mo.sample_size_score * 100).toFixed(0)}%</span>}
              {mo.model_disagreement_penalty > 0 && <span className="text-amber-600">Penalita modello: -{(mo.model_disagreement_penalty * 100).toFixed(0)}%</span>}
              {mo.uncertainty_penalty > 0 && <span className="text-amber-600">Incertezza: -{(mo.uncertainty_penalty * 100).toFixed(0)}%</span>}
            </div>
            {/* Validation warnings */}
            {(mo.confidence_warnings || []).length > 0 && (
              <div className="mt-2 space-y-1" data-testid="confidence-warnings">
                {mo.confidence_warnings.map((w, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[10px] text-amber-500 bg-amber-500/[0.06] border border-amber-500/15 rounded px-2 py-1">
                    <Activity size={10} className="shrink-0" />
                    {w}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Model Signals */}
          {mo.explanation_json && (
            <div className="pt-3" style={{ borderTop: '1px solid #27272A' }}>
              <div className="text-xs text-zinc-500 mb-2">Segnali del modello:</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(mo.explanation_json.model_signals || {}).map(([k, v]) => (
                  <span key={k} className="px-2 py-1 bg-zinc-800 rounded text-xs font-mono">
                    <span className="text-zinc-500">{k}:</span>{' '}
                    <span className="text-zinc-300">{(v * 100).toFixed(1)}%</span>
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {(mo.explanation_json.data_sources || []).map((s, i) => (
                  <span key={i} className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 rounded text-[10px]">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* LR vs V2 Comparison */}
      {lr && mo && <LRCompareSection lr={lr} mo={mo} />}

      {/* Features Summary */}
      {features && (
        <div className="rounded-sm p-4" style={{ background: '#18181B', border: '1px solid #27272A' }}>
          <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2 mb-3">
            <Activity size={13} /> Features Calcolate
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <FeatureItem label="H2H Totale" value={features.h2h_total_matches} />
            <FeatureItem label="H2H Vinte P1" value={features.h2h_p1_wins} />
            <FeatureItem label="H2H Vinte P2" value={features.h2h_p2_wins} />
            <FeatureItem label={`P1 WR Ultime 5${features.p1_recent_count < 5 ? ` (${features.p1_recent_count})` : ''}`} value={fmtProb(features.p1_last5_winrate)} />
            <FeatureItem label={`P2 WR Ultime 5${features.p2_recent_count < 5 ? ` (${features.p2_recent_count})` : ''}`} value={fmtProb(features.p2_last5_winrate)} />
            <FeatureItem label={`P1 WR Ultime 10${features.p1_recent_count < 10 ? ` (${features.p1_recent_count})` : ''}`} value={fmtProb(features.p1_last10_winrate)} />
            <FeatureItem label={`P2 WR Ultime 10${features.p2_recent_count < 10 ? ` (${features.p2_recent_count})` : ''}`} value={fmtProb(features.p2_last10_winrate)} />
            <FeatureItem label="P1 Diff Set" value={features.p1_sets_diff_recent?.toFixed(1)} />
            <FeatureItem label="P2 Diff Set" value={features.p2_sets_diff_recent?.toFixed(1)} />
            <FeatureItem label="P1 Strength" value={features.p1_recent_strength_score?.toFixed(0)} />
            <FeatureItem label="P2 Strength" value={features.p2_recent_strength_score?.toFixed(0)} />
            <FeatureItem label="Prob. Mercato" value={`${fmtProb(features.market_prob_p1)} / ${fmtProb(features.market_prob_p2)}`} />
          </div>

          {/* Same-Day Freshness — visible only when freshness data is present.
              Shadow mode: surfaces rhythm/fatigue without auto-blocking picks. */}
          {(features.p1_matches_today != null || features.p2_matches_today != null) && (
            <div className="mt-3 pt-3 border-t border-zinc-800" data-testid="match-detail-freshness">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                Same-Day Freshness
                {features.rhythm_edge_side === 'p1' && (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 text-[10px]"
                        data-testid="match-detail-rhythm-p1">rhythm → P1</span>
                )}
                {features.rhythm_edge_side === 'p2' && (
                  <span className="px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 text-[10px]"
                        data-testid="match-detail-rhythm-p2">rhythm → P2</span>
                )}
                {features.fatigue_warning && (
                  <span className="px-1.5 py-0.5 rounded bg-rose-900/40 text-rose-300 text-[10px]"
                        data-testid="match-detail-fatigue">
                    fatigue: {features.fatigue_warning}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                <FeatureItem
                  label="P1 Match Oggi"
                  value={`${features.p1_matches_today ?? 0}${features.p1_same_day_set_diff != null ? ` (Δset ${features.p1_same_day_set_diff > 0 ? '+' : ''}${features.p1_same_day_set_diff})` : ''}`}
                />
                <FeatureItem
                  label="P2 Match Oggi"
                  value={`${features.p2_matches_today ?? 0}${features.p2_same_day_set_diff != null ? ` (Δset ${features.p2_same_day_set_diff > 0 ? '+' : ''}${features.p2_same_day_set_diff})` : ''}`}
                />
                <FeatureItem
                  label="P1 WR Same-Day"
                  value={features.p1_same_day_winrate != null ? fmtProb(features.p1_same_day_winrate) : '—'}
                />
                <FeatureItem
                  label="P2 WR Same-Day"
                  value={features.p2_same_day_winrate != null ? fmtProb(features.p2_same_day_winrate) : '—'}
                />
                <FeatureItem
                  label="P1 Fresh WR10"
                  value={features.p1_fresh_form_weighted_last10 != null ? fmtProb(features.p1_fresh_form_weighted_last10) : '—'}
                />
                <FeatureItem
                  label="P2 Fresh WR10"
                  value={features.p2_fresh_form_weighted_last10 != null ? fmtProb(features.p2_fresh_form_weighted_last10) : '—'}
                />
                <FeatureItem
                  label="P1 Rest (min)"
                  value={features.p1_minutes_since_last_match != null ? Math.round(features.p1_minutes_since_last_match) : '—'}
                />
                <FeatureItem
                  label="P2 Rest (min)"
                  value={features.p2_minutes_since_last_match != null ? Math.round(features.p2_minutes_since_last_match) : '—'}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* H2H History */}
      <div data-testid="h2h-section" className="rounded-sm p-4" style={{ background: '#18181B', border: '1px solid #27272A' }}>
        <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2 mb-3">
          <Users size={13} /> Head to Head ({h2h_history?.length || 0} precedenti)
        </h3>

        {/* Summary band — always visible if there's at least 1 H2H */}
        {h2h_summary && h2h_summary.total > 0 && (
          <div data-testid="h2h-summary" className="mb-3 rounded bg-zinc-800/40 border border-zinc-700/40 p-2.5 text-[11px]">
            <div className="text-zinc-500 mb-1.5">
              Lato modello: <span className="text-zinc-200 font-semibold">{h2h_summary.model_side_player_name}</span>
              <span className="text-zinc-600"> ({h2h_summary.model_side?.toUpperCase()})</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <div className="text-zinc-600 text-[10px] uppercase">Totale</div>
                <div className="text-zinc-200 font-mono text-sm">{h2h_summary.total}</div>
              </div>
              <div>
                <div className="text-zinc-600 text-[10px] uppercase">Lato modello W-L</div>
                <div className="font-mono text-sm">
                  <span className="text-emerald-400">{h2h_summary.model_side_wins}</span>
                  <span className="text-zinc-600">-</span>
                  <span className="text-rose-400">{h2h_summary.model_side_losses}</span>
                </div>
              </div>
              <div>
                <div className="text-zinc-600 text-[10px] uppercase">Ultimi 3</div>
                <div className="text-zinc-200 font-mono text-sm">{h2h_summary.last3_record}</div>
              </div>
              <div>
                <div className="text-zinc-600 text-[10px] uppercase">Margine medio (3)</div>
                <div className={`font-mono text-sm ${(h2h_summary.last3_avg_margin_model_side ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {h2h_summary.last3_avg_margin_model_side != null
                    ? (h2h_summary.last3_avg_margin_model_side > 0 ? '+' : '') + h2h_summary.last3_avg_margin_model_side
                    : '—'}
                </div>
              </div>
            </div>
          </div>
        )}

        {(!h2h_history || h2h_history.length === 0) ? (
          <p className="text-xs text-zinc-600">Nessun precedente disponibile tra questi giocatori</p>
        ) : (
          <div className="space-y-1.5">
            {h2h_history.map((h, i) => {
              const date = h.previous_match_date ? h.previous_match_date.slice(0, 10) : '—';
              const tour = h.previous_tournament_name || '—';
              const winner = h.winner_name || '—';
              const loser = h.loser_name || '—';
              const score = h.score_display || `${h.player1_sets}-${h.player2_sets}`;
              const modelSideWon = h.model_side_won;
              const margin = h.set_margin_model_side;
              const marginStr = margin != null ? (margin > 0 ? `+${margin}` : `${margin}`) : '—';
              return (
                <div
                  key={i}
                  data-testid={`h2h-row-${i}`}
                  className={`rounded p-2 border text-[11px] ${
                    modelSideWon
                      ? 'bg-emerald-900/10 border-emerald-500/20'
                      : 'bg-rose-900/10 border-rose-500/20'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span className="text-zinc-500 font-mono text-[10px] shrink-0">{date}</span>
                      <span className="text-zinc-600 text-[10px] truncate max-w-[140px]" title={tour}>{tour}</span>
                    </div>
                    <span
                      data-testid={`h2h-badge-${i}`}
                      className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${
                        modelSideWon
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                          : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                      }`}
                    >
                      {modelSideWon ? 'Lato modello WON' : 'Lato modello LOST'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1.5 flex-wrap">
                    <div className="text-zinc-200 truncate">
                      <span className="font-semibold">{winner}</span>
                      <span className="text-zinc-500 mx-1">def.</span>
                      <span className="text-zinc-400">{loser}</span>
                      <span className="text-zinc-200 font-mono ml-2">{score}</span>
                    </div>
                    <div className="font-mono text-[10px]">
                      <span className="text-zinc-500">margine </span>
                      <span className={margin >= 0 ? 'text-emerald-400 font-semibold' : 'text-rose-400 font-semibold'}>
                        {marginStr}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Matches */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RecentMatchesList title={`Ultimi match - ${match.player1_name}`} matches={player1_recent} />
        <RecentMatchesList title={`Ultimi match - ${match.player2_name}`} matches={player2_recent} />
      </div>

      {/* Odds History */}
      {odds_history && odds_history.length > 0 && (
        <div className="rounded-sm p-4" style={{ background: '#18181B', border: '1px solid #27272A' }}>
          <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Storico Quote</h3>
          <div className="space-y-1">
            {odds_history.map((o, i) => (
              <div key={i} className="flex items-center gap-4 text-xs py-1 px-2 rounded bg-zinc-800/20">
                <span className="text-zinc-500 w-36">{new Date(o.scraped_at).toLocaleString('it-IT')}</span>
                <span className="text-zinc-400">{o.bookmaker_name}</span>
                <span className="font-mono text-zinc-300 ml-auto">P1: {fmt(o.odd_p1_decimal)} | P2: {fmt(o.odd_p2_decimal)}</span>
                <span className="text-zinc-600 font-mono">
                  ({fmtProb(o.implied_prob_p1_no_margin)} / {fmtProb(o.implied_prob_p2_no_margin)})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ===== MODEL VERDICT COMPARISON ===== */}
      {lr && mo && <ModelVerdictBar lr={lr} mo={mo} p1={match.player1_name} p2={match.player2_name} />}

      {/* ===== KELLY / STAKE RECOMMENDATIONS ===== */}
      <KellySection recommendations={data.recommendations || []} onPlaceBet={(rec) => { setModalRec(rec); setModalOpen(true); }} />

      {/* ===== AI ADVISOR ===== */}
      <AIAdvisorMatch matchId={match.id} />

      {/* ===== TOTALS / OVER-UNDER ===== */}
      <TotalsSection totalsData={totalsData} p1Name={match.player1_name} />

      <PlaceBetModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={fetchData}
        matchId={match.id}
        recommendation={modalRec}
        matchInfo={{ player1: match.player1_name, player2: match.player2_name, tournament: match.tournament_name }}
      />
    </div>
  );
}


// ===== Model Verdict Bar =====

function ModelVerdictBar({ lr, mo, p1, p2 }) {
  const lrP1 = lr.lr_final_p1 || 0.5;
  const v2P1 = mo.final_prob_p1 || 0.5;

  const lrEv1 = lr.lr_ev_p1 || 0;
  const lrEv2 = lr.lr_ev_p2 || 0;
  const v2Ev1 = mo.ev_p1_pct || 0;
  const v2Ev2 = mo.ev_p2_pct || 0;

  const lrBestEv = Math.max(lrEv1, lrEv2);
  const v2BestEv = Math.max(v2Ev1, v2Ev2);

  const lrSide = lrBestEv > 0 ? (lrEv1 >= lrEv2 ? 'P1' : 'P2') : null;
  const v2Side = v2BestEv > 0 ? (v2Ev1 >= v2Ev2 ? 'P1' : 'P2') : null;

  const lrName = lrSide === 'P1' ? p1 : lrSide === 'P2' ? p2 : null;
  const v2Name = v2Side === 'P1' ? p1 : v2Side === 'P2' ? p2 : null;

  const agree = lrSide && v2Side && lrSide === v2Side;
  const bothNoBet = !lrSide && !v2Side;

  return (
    <div data-testid="model-verdict-bar" className="rounded-sm p-4" style={{ background: '#18181B', border: '1px solid #27272A' }}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-3 font-semibold">Verdetto Modelli MW</div>
      <div className="grid grid-cols-2 gap-3">
        {/* LR Verdict */}
        <div className="rounded-sm p-3 flex items-center gap-3" style={{ background: '#0D0D0F', border: '1px solid #1F1F22', borderLeft: '3px solid #6366F1' }}>
          <div className="flex-1">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[10px] font-semibold text-indigo-400 uppercase tracking-wider">Logistic Regression</span>
              <span className="text-[8px] px-1 py-0.5 rounded bg-indigo-500/10 text-indigo-400">v1</span>
            </div>
            {lrBestEv > 0 && lrName ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-zinc-100">{lrName.split(' ').pop()}</span>
                <span className="text-xs font-mono text-emerald-400">+{lrBestEv.toFixed(1)}%</span>
                <span className="text-[10px] text-zinc-500">({(lrP1 * 100).toFixed(1)}%)</span>
              </div>
            ) : (
              <span className="text-xs text-zinc-500 italic">Nessun valore</span>
            )}
          </div>
        </div>

        {/* Pascal Verdict */}
        <div className="rounded-sm p-3 flex items-center gap-3" style={{ background: '#0D0D0F', border: '1px solid #1F1F22', borderLeft: '3px solid #10B981' }}>
          <div className="flex-1">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Pascal-Sierpinski</span>
              <span className="text-[8px] px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-400">v2</span>
              <span className="text-[8px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400">OPERATIVO</span>
            </div>
            {v2BestEv > 0 && v2Name ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-zinc-100">{v2Name.split(' ').pop()}</span>
                <span className="text-xs font-mono text-emerald-400">+{v2BestEv.toFixed(1)}%</span>
                <span className="text-[10px] text-zinc-500">({(v2P1 * 100).toFixed(1)}%)</span>
              </div>
            ) : (
              <span className="text-xs text-zinc-500 italic">Nessun valore</span>
            )}
          </div>
        </div>
      </div>

      {/* Agreement indicator */}
      {!bothNoBet && (
        <div className="mt-2 text-center">
          {agree && lrSide && v2Side ? (
            <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 font-medium">
              ACCORDO — Entrambi puntano su {lrName?.split(' ').pop()}
            </span>
          ) : lrSide && v2Side && !agree ? (
            <span className="text-[10px] px-2 py-1 rounded bg-red-500/10 text-red-400 font-medium">
              DIVERGENZA — LR: {lrName?.split(' ').pop()} vs Pascal: {v2Name?.split(' ').pop()}
            </span>
          ) : lrSide && !v2Side ? (
            <span className="text-[10px] px-2 py-1 rounded bg-indigo-500/10 text-indigo-300 font-medium">
              SOLO LR vede valore — Pascal non ha EV+
            </span>
          ) : !lrSide && v2Side ? (
            <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/10 text-emerald-300 font-medium">
              SOLO PASCAL vede valore — LR non ha EV+
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

// ===== LR vs V2 Comparison Section =====

function LRCompareSection({ lr, mo }) {
  const lrP1 = lr.lr_final_p1 || 0.5;
  const v2P1 = mo.final_prob_p1 || 0.5;
  const diffPct = ((lrP1 - v2P1) * 100).toFixed(1);
  const diffColor = Math.abs(diffPct) < 1 ? 'text-zinc-500' : diffPct > 0 ? 'text-indigo-400' : 'text-emerald-400';

  const lrEv1 = lr.lr_ev_p1 || 0;
  const lrEv2 = lr.lr_ev_p2 || 0;
  const v2Ev1 = mo.ev_p1_pct || 0;
  const v2Ev2 = mo.ev_p2_pct || 0;
  const lrBest = Math.max(lrEv1, lrEv2);
  const v2Best = Math.max(v2Ev1, v2Ev2);
  const lrSide = lr.lr_recommended_side || '-';
  const v2Side = v2Ev1 >= v2Ev2 ? 'P1' : 'P2';
  const agree = lrSide === v2Side;

  const feat = lr.lr_features || {};

  return (
    <div data-testid="lr-compare-section" className="rounded-sm p-4" style={{ background: '#18181B', border: '1px solid #27272A' }}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2">
          <GitCompare size={13} className="text-indigo-400" /> LR vs Pascal — Confronto MW
        </h3>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${agree ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
            {agree ? 'ACCORDO' : 'DIVERGENZA'}
          </span>
        </div>
      </div>

      {/* Side by side probabilities */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        {/* LR Column */}
        <div className="rounded-sm p-3" style={{ background: '#0D0D0F', border: '1px solid #1F1F22', borderTop: '2px solid #6366F1' }}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-indigo-300" style={{ fontFamily: 'Outfit, sans-serif' }}>Logistic Regression</span>
            <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-500/10 text-indigo-400">v1</span>
          </div>
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-zinc-500">Prob. Finale P1</span>
              <span className="font-mono text-indigo-300 font-semibold">{fmtProb(lrP1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Fair Odds P1</span>
              <span className="font-mono text-indigo-300">{fmt(lr.lr_fair_odds_p1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Miglior EV</span>
              <span className={`font-mono font-semibold ${lrBest > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                {lrBest > 0 ? `+${lrBest.toFixed(1)}%` : '—'} {lrSide}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Confidence</span>
              <span className="font-mono text-indigo-300">{((lr.lr_confidence || 0) * 100).toFixed(0)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Alpha</span>
              <span className="font-mono text-zinc-400">{(lr.lr_alpha || 0).toFixed(3)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">z-score</span>
              <span className="font-mono text-zinc-400">{(lr.lr_z_score || 0).toFixed(4)}</span>
            </div>
          </div>
        </div>

        {/* V2 Column */}
        <div className="rounded-sm p-3" style={{ background: '#0D0D0F', border: '1px solid #1F1F22', borderTop: '2px solid #10B981' }}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-emerald-300" style={{ fontFamily: 'Outfit, sans-serif' }}>Pascal-Sierpinski</span>
            <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-500/10 text-emerald-400">v2</span>
          </div>
          <div className="space-y-1.5 text-xs">
            <div className="flex justify-between">
              <span className="text-zinc-500">Prob. Finale P1</span>
              <span className="font-mono text-emerald-300 font-semibold">{fmtProb(v2P1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Fair Odds P1</span>
              <span className="font-mono text-emerald-300">{fmt(mo.fair_odd_p1)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Miglior EV</span>
              <span className={`font-mono font-semibold ${v2Best > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                {v2Best > 0 ? `+${v2Best.toFixed(1)}%` : '—'} {v2Side}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Confidence</span>
              <span className="font-mono text-emerald-300">{((mo.confidence_score || 0) * 100).toFixed(0)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Data Quality</span>
              <span className="font-mono text-zinc-400">{((mo.data_quality_score || 0) * 100).toFixed(0)}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-500">Prop. Strength</span>
              <span className="font-mono text-zinc-400">{((mo.proprietary_strength_score || mo.signal_strength_score || 0) * 100).toFixed(0)}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Diff summary bar */}
      <div className="flex items-center gap-3 px-3 py-2 rounded-sm text-xs" style={{ background: '#0D0D0F', border: '1px solid #1F1F22' }}>
        <span className="text-zinc-500">Differenza P1:</span>
        <span className={`font-mono font-bold ${diffColor}`}>{diffPct > 0 ? '+' : ''}{diffPct}%</span>
        <span className="text-zinc-700">|</span>
        <span className="text-zinc-500">LR EV:</span>
        <span className="font-mono text-indigo-300">{lrBest > 0 ? `+${lrBest.toFixed(1)}%` : '—'}</span>
        <span className="text-zinc-700">|</span>
        <span className="text-zinc-500">V2 EV:</span>
        <span className="font-mono text-emerald-300">{v2Best > 0 ? `+${v2Best.toFixed(1)}%` : '—'}</span>
      </div>

      {/* LR Feature z-scores */}
      {Object.keys(feat).length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1.5">LR Feature z-scores</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(feat).filter(([k]) => k !== 'baseline_p1').map(([k, v]) => {
              const barColor = v > 0.5 ? 'text-indigo-400' : v < -0.5 ? 'text-rose-400' : 'text-zinc-400';
              return (
                <span key={k} className="px-2 py-1 bg-zinc-800/50 rounded text-[10px] font-mono" data-testid={`lr-feat-${k}`}>
                  <span className="text-zinc-500">{k.replace('_diff', '').replace('_', ' ')}:</span>{' '}
                  <span className={barColor}>{typeof v === 'number' ? v.toFixed(3) : v}</span>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Kelly / Stake Recommendations Section =====

const KELLY_MODE_LABELS = { full: 'Full Kelly', half: 'Half Kelly', quarter: 'Quarter Kelly' };
const MKT_LABELS = { match_winner: 'Match Winner', over_under: 'Over/Under', handicap: 'Handicap' };
const MKT_BADGE_CLS = {
  match_winner: 'bg-blue-500/15 text-blue-400',
  over_under: 'bg-amber-500/15 text-amber-400',
  handicap: 'bg-purple-500/15 text-purple-400',
};

function KellySection({ recommendations, onPlaceBet }) {
  if (!recommendations || recommendations.length === 0) return null;

  return (
    <div data-testid="kelly-section" className="rounded-sm overflow-hidden" style={{ background: '#18181B', border: '1px solid #27272A' }}>
      <div className="px-5 py-4 flex items-center gap-2" style={{ borderBottom: '1px solid #27272A' }}>
        <TrendingUp size={14} className="text-emerald-400" />
        <h2 className="font-semibold text-zinc-200 text-sm">Giocate Consigliate (Kelly)</h2>
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-medium">Pascal-Sierpinski v2</span>
        <span className="text-xs text-zinc-500 ml-auto">Bankroll: {recommendations[0]?.bankroll_amount}€</span>
      </div>
      <div className="divide-y" style={{ borderColor: '#1F1F22' }}>
        {recommendations.map((rec, i) => {
          const isRecommended = rec.recommended_stake_rounded > 0;
          return (
            <div key={i} className={`px-5 py-4 ${isRecommended ? '' : 'opacity-40'}`} data-testid={`kelly-rec-${i}`}>
              {/* Header: market + selection */}
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded ${MKT_BADGE_CLS[rec.market_type] || 'bg-zinc-800 text-zinc-400'}`}>
                  {MKT_LABELS[rec.market_type] || rec.market_type}
                </span>
                <span className="text-sm font-medium text-zinc-200">{rec.selection_name}</span>
                {rec.line_value != null && rec.market_type === 'handicap' && (
                  <span className="text-xs text-zinc-500">Linea: {rec.line_value > 0 ? '+' : ''}{rec.line_value}</span>
                )}
                {rec.line_value != null && rec.market_type !== 'handicap' && (
                  <span className="text-xs text-zinc-500">Linea: {rec.line_value}</span>
                )}
                <span className="text-xs text-zinc-500 ml-auto">EV {fmtPct(rec.ev_pct)}</span>
              </div>

              {/* Kelly breakdown table */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {['full', 'half', 'quarter'].map(mode => {
                  const isActive = rec.recommended_kelly_mode === mode;
                  const raw = rec[`stake_${mode}_raw`];
                  const rounded = rec[`stake_${mode}_rounded`];
                  const fraction = rec[`kelly_${mode}_fraction`];
                  return (
                    <div key={mode} className={`rounded-sm p-2.5 ${isActive ? 'bg-emerald-500/[0.08] border border-emerald-500/25' : 'bg-zinc-900/50'}`}>
                      <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">
                        {KELLY_MODE_LABELS[mode]}
                        {isActive && <span className="text-emerald-400 ml-1">CONSIGLIATA</span>}
                      </div>
                      <div className={`text-base font-bold ${isActive ? 'text-emerald-400' : 'text-zinc-400'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {rounded > 0 ? `${rounded.toFixed(2)}€` : '—'}
                      </div>
                      <div className="text-[9px] text-zinc-600 mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        Raw: {raw.toFixed(2)}€ | f={fraction ? (fraction * 100).toFixed(2) : 0}%
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Reason + metadata + place bet */}
              {isRecommended && (
                <div className="flex items-center gap-3 flex-wrap text-[10px]">
                  <span className="text-zinc-400">
                    Motivo: <span className="text-zinc-300">{rec.recommended_kelly_reason}</span>
                  </span>
                  <span className="text-zinc-600">
                    Rischio: {(rec.risk_score * 100).toFixed(0)}%
                  </span>
                  <span className="text-zinc-600">
                    Qualita dati: {(rec.data_quality_score * 100).toFixed(0)}%
                  </span>
                  <span className="text-zinc-600">
                    Arrot: {rec.rounding_mode} (step {rec.bet_step}€)
                  </span>
                  <button
                    onClick={() => onPlaceBet?.(rec)}
                    data-testid={`place-bet-detail-${i}`}
                    className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-sm text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 hover:bg-emerald-500/25 transition-colors"
                  >
                    <PlusCircle size={11} />
                    Segna come Giocata
                  </button>
                </div>
              )}
              {(rec.recommendation_warnings || []).length > 0 && (
                <div className="mt-2 space-y-1" data-testid={`rec-warnings-${i}`}>
                  {rec.recommendation_warnings.map((w, wi) => (
                    <div key={wi} className="flex items-center gap-1.5 text-[10px] text-amber-500 bg-amber-500/[0.06] border border-amber-500/15 rounded px-2 py-1">
                      <Activity size={10} className="shrink-0" />
                      {w}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}



// ===== Totals / Over-Under Section =====

function TotalsSection({ totalsData, p1Name }) {
  if (!totalsData || !totalsData.model) return null;
  const m = totalsData.model;
  const dp = m.distribution_payload || {};
  const factors = m.factors || {};
  const lines = totalsData.totals_lines || [];
  const hcLines = totalsData.handicap_lines || [];

  const linesValue = lines.filter(l => l.is_value_over || l.is_value_under);
  const hcValue = hcLines.filter(h => h.is_value_cover || h.is_value_not_cover);

  return (
    <>
      {/* Model Summary */}
      <div data-testid="totals-section" className="rounded-sm p-4" style={{ background: '#18181B', border: '1px solid #27272A' }}>
        <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider flex items-center gap-2 mb-4">
          <TrendingUp size={13} /> Totals / Over-Under (Monte Carlo)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          <MetricBox label="Linea Teorica" value={m.theoretical_points_line?.toFixed(1)} accent />
          <MetricBox label="Media Attesa" value={m.expected_total_points?.toFixed(1)} />
          <MetricBox label="Mediana" value={m.median_total_points} />
          <MetricBox label="Deviazione Std" value={m.std_dev_total_points?.toFixed(1)} />
          <MetricBox label="Simulazioni" value={(m.simulation_count || 0).toLocaleString()} />
        </div>

        {/* Set Distribution */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <SetProbBar label="3 Set" prob={dp.prob_3_sets} />
          <SetProbBar label="4 Set" prob={dp.prob_4_sets} />
          <SetProbBar label="5 Set" prob={dp.prob_5_sets} />
        </div>

        {/* Factors */}
        <div className="rounded-sm p-3 text-xs text-zinc-400 space-y-1" style={{ background: '#0D0D0F', border: '1px solid #1F1F22' }}>
          <div>Match: <span className="text-zinc-300">{factors.match_balance || '—'}</span> (P1 {((factors.match_prob_p1 || 0) * 100).toFixed(0)}%)</div>
          <div>Point win prob: <span className="font-mono text-zinc-300">{dp.point_win_prob_p1?.toFixed(4)}</span> | Set win prob: <span className="font-mono text-zinc-300">{dp.set_win_prob_p1?.toFixed(4)}</span></div>
          <div>Range punti: <span className="font-mono text-zinc-300">{dp.p10} - {dp.p90}</span> (P10-P90)</div>
          {factors.p1_form_last5 != null && <div>Forma: P1 {(factors.p1_form_last5 * 100).toFixed(0)}% | P2 {((factors.p2_form_last5 || 0) * 100).toFixed(0)}% (ultime 5)</div>}
          {factors.h2h_matches > 0 && <div>H2H: {factors.h2h_matches} match, P1 vince {factors.h2h_p1_wins}</div>}
        </div>
      </div>

      {/* O/U Lines Table */}
      {lines.length > 0 && (
        <div className="rounded-sm overflow-hidden" style={{ background: '#18181B', border: '1px solid #27272A' }}>
          <div className="px-4 py-3" style={{ borderBottom: '1px solid #27272A' }}>
            <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
              Over/Under Lines ({lines.length}) {linesValue.length > 0 && <span className="text-emerald-400 ml-2">{linesValue.length} con value</span>}
            </h3>
          </div>
          <table className="w-full text-xs" data-testid="totals-lines-table">
            <thead>
              <tr className="text-[10px] text-zinc-600 uppercase tracking-wider" style={{ borderBottom: '1px solid #27272A' }}>
                <th className="px-3 py-2 text-left">Linea</th>
                <th className="px-2 py-2 text-center">P(Over)</th>
                <th className="px-2 py-2 text-center">P(Under)</th>
                <th className="px-2 py-2 text-center">Fair Over</th>
                <th className="px-2 py-2 text-center">Fair Under</th>
                <th className="px-2 py-2 text-center">Book Over</th>
                <th className="px-2 py-2 text-center">Book Under</th>
                <th className="px-2 py-2 text-right">EV Over</th>
                <th className="px-2 py-2 text-right">EV Under</th>
                <th className="px-2 py-2 text-center">Lato</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const isTheo = l.line_points === m.theoretical_points_line;
                const debug = l.debug || {};
                const recSide = debug.recommended_side;
                return (
                  <tr key={i} className={`border-t border-zinc-800/30 ${isTheo ? 'bg-emerald-500/5' : ''} ${l.is_value_over || l.is_value_under ? 'bg-emerald-500/5' : ''}`}>
                    <td className="px-3 py-2 font-mono font-semibold text-zinc-200">
                      {l.line_points.toFixed(1)}
                      {isTheo && <span className="ml-1 text-[9px] text-emerald-400">THEO</span>}
                      {l.has_book_odds && <span className="ml-1 text-[9px] text-blue-400">BK</span>}
                      {debug.h2h_correction_applied && (
                        <span className="ml-1 text-[9px] text-amber-400" title={`MC raw: ${((debug.mc_prob_over_raw||0)*100).toFixed(1)}%, H2H smooth: ${((debug.h2h_prob_over_raw||0)*100).toFixed(1)}%, w=${debug.effective_h2h_weight}`}>H2H</span>
                      )}
                      {debug.h2h_adjustment_capped && <span className="ml-0.5 text-[9px] text-red-400">CAP</span>}
                    </td>
                    <td className="px-2 py-2 text-center font-mono text-zinc-300">{(l.prob_over * 100).toFixed(1)}%</td>
                    <td className="px-2 py-2 text-center font-mono text-zinc-300">{(l.prob_under * 100).toFixed(1)}%</td>
                    <td className="px-2 py-2 text-center font-mono text-zinc-400">{l.fair_over_odds.toFixed(2)}</td>
                    <td className="px-2 py-2 text-center font-mono text-zinc-400">{l.fair_under_odds.toFixed(2)}</td>
                    <td className="px-2 py-2 text-center font-mono text-zinc-300">{l.book_over_odds ? l.book_over_odds.toFixed(2) : '—'}</td>
                    <td className="px-2 py-2 text-center font-mono text-zinc-300">{l.book_under_odds ? l.book_under_odds.toFixed(2) : '—'}</td>
                    <td className={`px-2 py-2 text-right font-mono font-semibold ${l.ev_over_pct > 0 ? 'text-emerald-400' : l.book_over_odds ? 'text-rose-400' : 'text-zinc-600'}`}>
                      {l.book_over_odds ? `${l.ev_over_pct > 0 ? '+' : ''}${l.ev_over_pct.toFixed(1)}%` : '—'}
                    </td>
                    <td className={`px-2 py-2 text-right font-mono font-semibold ${l.ev_under_pct > 0 ? 'text-emerald-400' : l.book_under_odds ? 'text-rose-400' : 'text-zinc-600'}`}>
                      {l.book_under_odds ? `${l.ev_under_pct > 0 ? '+' : ''}${l.ev_under_pct.toFixed(1)}%` : '—'}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {recSide && recSide !== 'none' && (
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${recSide === 'over' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
                          {recSide === 'over' ? 'OVER' : 'UNDER'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Debug: MC Expected vs H2H */}
          {lines[0]?.debug?.mc_expected_total && (
            <div className="px-4 py-2 text-[10px] text-zinc-600 flex gap-4" style={{ borderTop: '1px solid #27272A' }}>
              <span>MC Expected: <span className="text-zinc-400 font-mono">{lines[0].debug.mc_expected_total}</span></span>
              <span>MC Median: <span className="text-zinc-400 font-mono">{lines[0].debug.mc_median_total}</span></span>
              {lines[0].debug.h2h_mean_pts && <span>H2H Mean Pts: <span className="text-zinc-400 font-mono">{lines[0].debug.h2h_mean_pts}</span></span>}
              {lines[0].debug.h2h_adjustment_capped && <span className="text-amber-500">H2H weight capped</span>}
              {lines.some(l => (l.sanity_warnings || []).length > 0) && (
                <span className="text-red-400">Sanity warnings active</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Handicap Lines Table */}
      {hcLines.length > 0 && (
        <div className="rounded-sm overflow-hidden" style={{ background: '#18181B', border: '1px solid #27272A' }}>
          <div className="px-4 py-3" style={{ borderBottom: '1px solid #27272A' }}>
            <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
              Handicap Set ({hcLines.length}) {hcValue.length > 0 && <span className="text-emerald-400 ml-2">{hcValue.length} con value</span>}
            </h3>
          </div>
          <table className="w-full text-xs" data-testid="handicap-lines-table">
            <thead>
              <tr className="text-[10px] text-zinc-600 uppercase tracking-wider" style={{ borderBottom: '1px solid #27272A' }}>
                <th className="px-3 py-2 text-left">HC {p1Name?.split(' ').pop()}</th>
                <th className="px-2 py-2 text-center">P(Cover)</th>
                <th className="px-2 py-2 text-center">P(Not)</th>
                <th className="px-2 py-2 text-center">Fair Cover</th>
                <th className="px-2 py-2 text-center">Fair Not</th>
                <th className="px-2 py-2 text-center">Book Home</th>
                <th className="px-2 py-2 text-center">Book Away</th>
                <th className="px-2 py-2 text-right">EV Cover</th>
                <th className="px-2 py-2 text-right">EV Not</th>
                <th className="px-2 py-2 text-center">MC Raw</th>
              </tr>
            </thead>
            <tbody>
              {hcLines.map((h, i) => {
                const dbg = h.debug || {};
                const sd = dbg.score_distribution || {};
                const sw = h.sanity_warnings || [];
                const h2hApplied = dbg.h2h_correction_applied;
                return (
                  <React.Fragment key={i}>
                    <tr className={`border-t border-zinc-800/30 ${h.is_value_cover || h.is_value_not_cover ? 'bg-emerald-500/5' : ''}`}>
                      <td className="px-3 py-2 font-mono font-semibold text-zinc-200">
                        {h.handicap > 0 ? '+' : ''}{h.handicap.toFixed(1)}
                        {h.has_book_odds && <span className="ml-1 text-[9px] text-blue-400">BK</span>}
                        {h2hApplied && <span className="ml-1 text-[9px] text-amber-400">H2H</span>}
                      </td>
                      <td className="px-2 py-2 text-center font-mono text-zinc-300">{(h.prob_cover * 100).toFixed(1)}%</td>
                      <td className="px-2 py-2 text-center font-mono text-zinc-300">{(h.prob_not_cover * 100).toFixed(1)}%</td>
                      <td className="px-2 py-2 text-center font-mono text-zinc-400">{h.fair_cover_odds.toFixed(2)}</td>
                      <td className="px-2 py-2 text-center font-mono text-zinc-400">{h.fair_not_cover_odds.toFixed(2)}</td>
                      <td className="px-2 py-2 text-center font-mono text-zinc-300">{h.book_home_odds ? h.book_home_odds.toFixed(2) : '—'}</td>
                      <td className="px-2 py-2 text-center font-mono text-zinc-300">{h.book_away_odds ? h.book_away_odds.toFixed(2) : '—'}</td>
                      <td className={`px-2 py-2 text-right font-mono font-semibold ${h.ev_cover_pct > 0 ? 'text-emerald-400' : h.book_home_odds ? 'text-rose-400' : 'text-zinc-600'}`}>
                        {h.book_home_odds ? `${h.ev_cover_pct > 0 ? '+' : ''}${h.ev_cover_pct.toFixed(1)}%` : '—'}
                      </td>
                      <td className={`px-2 py-2 text-right font-mono font-semibold ${h.ev_not_cover_pct > 0 ? 'text-emerald-400' : h.book_away_odds ? 'text-rose-400' : 'text-zinc-600'}`}>
                        {h.book_away_odds ? `${h.ev_not_cover_pct > 0 ? '+' : ''}${h.ev_not_cover_pct.toFixed(1)}%` : '—'}
                      </td>
                      <td className="px-2 py-2 text-center font-mono text-zinc-500 text-[10px]">{dbg.mc_cover_raw != null ? (dbg.mc_cover_raw * 100).toFixed(1) + '%' : '—'}</td>
                    </tr>
                    {/* Debug row for HC line */}
                    {(h2hApplied || sw.length > 0 || Object.keys(sd).length > 0) && (
                      <tr className="border-0">
                        <td colSpan={10} className="px-3 py-1.5">
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px]">
                            {h2hApplied && (
                              <span className="text-amber-500/80">H2H: cover_rate={((dbg.h2h_cover_rate || 0) * 100).toFixed(0)}% n={dbg.h2h_count || 0} w={dbg.hc_h2h_weight}</span>
                            )}
                            {dbg.match_win_p1 != null && (
                              <span className="text-zinc-600">MW: {(dbg.match_win_p1 * 100).toFixed(1)}%</span>
                            )}
                            {dbg.analytical_cover_from_scores != null && (
                              <span className="text-zinc-600">Analitico: {(dbg.analytical_cover_from_scores * 100).toFixed(1)}%</span>
                            )}
                            {Object.keys(sd).length > 0 && (
                              <span className="text-zinc-600">
                                Score: {(sd.p1_3_0 * 100).toFixed(1)}|{(sd.p1_3_1 * 100).toFixed(1)}|{(sd.p1_3_2 * 100).toFixed(1)} vs {(sd.p2_3_0 * 100).toFixed(1)}|{(sd.p2_3_1 * 100).toFixed(1)}|{(sd.p2_3_2 * 100).toFixed(1)}
                              </span>
                            )}
                          </div>
                          {sw.map((w, wi) => (
                            <div key={wi} className="flex items-center gap-1.5 text-[10px] text-amber-500 bg-amber-500/[0.06] border border-amber-500/15 rounded px-2 py-0.5 mt-1">
                              <Activity size={10} className="shrink-0" />
                              {w}
                            </div>
                          ))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function MetricBox({ label, value, accent }) {
  return (
    <div className="rounded-sm p-3" style={{ background: '#0D0D0F', border: '1px solid #1F1F22' }}>
      <div className="text-zinc-500 text-[10px] uppercase tracking-wider">{label}</div>
      <div className={`text-lg font-bold mt-1 font-mono ${accent ? 'text-emerald-400' : 'text-zinc-200'}`}>{value ?? '—'}</div>
    </div>
  );
}

function SetProbBar({ label, prob }) {
  const pct = ((prob || 0) * 100);
  return (
    <div className="rounded-sm p-2" style={{ background: '#0D0D0F', border: '1px solid #1F1F22' }}>
      <div className="flex justify-between text-[10px] mb-1">
        <span className="text-zinc-500">{label}</span>
        <span className="text-zinc-300 font-mono">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500/40 rounded-full" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function PlayerCard({ player, side, bkOdd, fairOdd, fairProb, baselineProb, propProb, edge, ev, isBest }) {
  const hasValue = ev > 0;
  return (
    <div data-testid={`player-card-${side.toLowerCase()}`}
      className={`rounded-sm p-4 ${isBest && hasValue ? 'ring-1 ring-emerald-500/30' : ''}`}
      style={{ background: '#18181B', border: '1px solid #27272A' }}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-zinc-200">{player}</span>
        <span className="text-[10px] text-zinc-600 uppercase">{side}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div>
          <span className="text-zinc-500">Quota Bookmaker</span>
          <div className="font-mono text-lg font-bold text-zinc-200 mt-0.5">{fmt(bkOdd)}</div>
        </div>
        <div>
          <span className="text-zinc-500">Fair Odds</span>
          <div className="font-mono text-lg font-bold text-emerald-400 mt-0.5">{fmt(fairOdd)}</div>
        </div>
        <div>
          <span className="text-zinc-500">Edge</span>
          <div className={`font-mono font-semibold mt-0.5 ${edge > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtPct(edge)}</div>
        </div>
        <div>
          <span className="text-zinc-500">EV</span>
          <div className={`font-mono font-semibold mt-0.5 ${ev > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmtPct(ev)}</div>
        </div>
        <div>
          <span className="text-zinc-500">Probabilita Fair</span>
          <div className="font-mono text-zinc-300 mt-0.5">{fmtProb(fairProb)}</div>
        </div>
        <div>
          <span className="text-zinc-500">Prob. Implicita BK</span>
          <div className="font-mono text-zinc-400 mt-0.5">{bkOdd > 0 ? fmtProb(1 / bkOdd) : '—'}</div>
        </div>
      </div>
    </div>
  );
}

function ConfBar({ score, color }) {
  const pct = Math.round((score || 0) * 100);
  const colorClasses = {
    blue: pct >= 60 ? 'bg-blue-500' : pct >= 30 ? 'bg-blue-500/60' : 'bg-zinc-600',
    amber: pct >= 60 ? 'bg-amber-500' : pct >= 30 ? 'bg-amber-500/60' : 'bg-zinc-600',
    purple: pct >= 60 ? 'bg-purple-500' : pct >= 30 ? 'bg-purple-500/60' : 'bg-zinc-600',
  };
  const barClass = color ? colorClasses[color] || colorClasses.blue : (pct >= 60 ? 'bg-emerald-500' : pct >= 30 ? 'bg-amber-500' : 'bg-zinc-600');
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${barClass}`}
          style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-xs text-zinc-400">{pct}%</span>
    </div>
  );
}

function FeatureItem({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1 px-2 bg-zinc-800/30 rounded">
      <span className="text-zinc-500">{label}</span>
      <span className="font-mono text-zinc-300">{value ?? '—'}</span>
    </div>
  );
}

function RecentMatchesList({ title, matches }) {
  return (
    <div className="rounded-sm p-4" style={{ background: '#18181B', border: '1px solid #27272A' }}>
      <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">{title}</h3>
      {(!matches || matches.length === 0) ? (
        <p className="text-xs text-zinc-600">Nessun match recente</p>
      ) : (
        <div className="space-y-1">
          {matches.slice(0, 10).map((m, i) => (
            <div key={i} className="flex items-center gap-2 text-xs py-1 px-2 rounded bg-zinc-800/20">
              <span className={`w-5 h-5 flex items-center justify-center rounded text-[10px] font-bold ${
                m.result_wl === 'W' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'
              }`}>{m.result_wl}</span>
              <span className="text-zinc-400 flex-1 truncate">vs {m.opponent_name}</span>
              <span className="text-zinc-500 font-mono">{m.result_text}</span>
              <span className="text-zinc-600 w-16 text-right">{m.previous_match_date?.slice(0, 10) || ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
